import { Card, Col, Form, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  getAllIndustries,
  getAllCategoryTypesByIndustry,
  getAllCategoriesByCategoryType,
  getAllSubCategoriesByCategory,
  getAllTagsByIndustry,
  Industry,
  CategoryType,
  Category,
  SubCategory,
  Brand,
  Tag,
  getAllBrands
} from 'api/categories/_requests';
import { useFormContext, Controller } from 'react-hook-form';
import { AddProductSchema } from 'pages/apps/e-commerce/admin/model';
import ReactSelect from 'components/base/ReactSelect';

type SelectOption = {
  value: string;
  label: string;
};

const OrganizeFormCard = ({ className }: { className?: string }) => {
  const { control, watch, formState } = useFormContext<AddProductSchema>();

  const selectedIndustry = watch('industry');
  const selectedCategoryType = watch('categoryType');
  const selectedCategory = watch('category');
  const selectedTags = watch('tags');

  const { data: industries } = useQuery<Industry[]>({
    queryKey: ['industries'],
    queryFn: getAllIndustries
  });

  const { data: categoryTypes } = useQuery<CategoryType[]>({
    queryKey: ['categoryTypes', selectedIndustry],
    queryFn: () => getAllCategoryTypesByIndustry(selectedIndustry || '')
    // enabled: !!selectedIndustry
  });
  const { data: categories } = useQuery<Category[]>({
    queryKey: ['categories', selectedCategoryType],
    queryFn: () => getAllCategoriesByCategoryType(selectedCategoryType),
    enabled: !!selectedCategoryType
  });

  const { data: subCategories } = useQuery<SubCategory[]>({
    queryKey: ['subCategories', selectedCategory],
    queryFn: () => getAllSubCategoriesByCategory(selectedCategory),
    enabled: !!selectedCategory
  });

  const { data: brands } = useQuery<Brand[]>({
    queryKey: ['all-brands'],
    queryFn: getAllBrands
  });

  const { data: tags } = useQuery<Tag[]>({
    queryKey: ['tags', selectedIndustry],
    queryFn: () => getAllTagsByIndustry(selectedIndustry || ''),
    enabled: !!selectedIndustry
  });

  return (
    <Card className={className}>
      <Card.Body>
        <h4 className="mb-4">Organize</h4>
        <Row className="gx-3 gy-4">
          <Col xs={12} sm={6} xl={12}>
            <div className="d-flex gap-2 mb-2">
              <h5 className="mb-0 text-body-highlight">Industry</h5>
            </div>
            <Controller
              name="industry"
              control={control}
              render={({ field }) => (
                <ReactSelect
                  {...field}
                  aria-label="industry"
                  options={industries?.map(industry => ({
                    value: industry._id,
                    label: industry.name
                  }))}
                  placeholder="Select Industry"
                  value={
                    field.value
                      ? {
                          value: field.value,
                          label: industries?.find(
                            industry => industry._id === field.value
                          )?.name
                        }
                      : null
                  }
                  onChange={(newValue: unknown) => {
                    const option = newValue as SelectOption;
                    field.onChange(option?.value || null);
                  }}
                />
              )}
            />
            <div className="text-danger">
              {formState.errors.industry?.message}
            </div>
          </Col>

          <Col xs={12} sm={6} xl={12}>
            <div className="d-flex gap-2 mb-2">
              <h5 className="mb-0 text-body-highlight">Category Type</h5>
            </div>
            <Controller
              name="categoryType"
              control={control}
              render={({ field }) => (
                <ReactSelect
                  {...field}
                  aria-label="categoryType"
                  options={categoryTypes?.map(categoryType => ({
                    value: categoryType._id,
                    label: categoryType.name
                  }))}
                  placeholder="Select Category Type"
                  // isDisabled={!selectedIndustry}
                  value={
                    field.value
                      ? {
                          value: field.value,
                          label: categoryTypes?.find(
                            categoryType => categoryType._id === field.value
                          )?.name
                        }
                      : null
                  }
                  onChange={(newValue: unknown) => {
                    const option = newValue as SelectOption;
                    field.onChange(option?.value || null);
                  }}
                />
              )}
            />
            <div className="text-danger">
              {formState.errors.categoryType?.message}
            </div>
          </Col>

          <Col xs={12} sm={6} xl={12}>
            <div className="d-flex gap-2 mb-2">
              <h5 className="mb-0 text-body-highlight">Category</h5>
            </div>
            <Controller
              name="category"
              control={control}
              render={({ field }) => (
                <ReactSelect
                  {...field}
                  aria-label="category"
                  options={categories?.map(category => ({
                    value: category._id,
                    label: category.name
                  }))}
                  placeholder="Select Category"
                  isDisabled={!selectedCategoryType}
                  value={
                    field.value
                      ? {
                          value: field.value,
                          label: categories?.find(
                            category => category._id === field.value
                          )?.name
                        }
                      : null
                  }
                  onChange={(newValue: unknown) => {
                    const option = newValue as SelectOption;
                    field.onChange(option?.value || null);
                  }}
                />
              )}
            />
            <div className="text-danger">
              {formState.errors.category?.message}
            </div>
          </Col>

          <Col xs={12} sm={6} xl={12}>
            <div className="d-flex gap-2 mb-2">
              <h5 className="mb-0 text-body-highlight">Sub Category</h5>
            </div>
            <Controller
              name="subCategory"
              control={control}
              render={({ field }) => (
                <ReactSelect
                  {...field}
                  aria-label="subCategory"
                  options={subCategories?.map(subCategory => ({
                    value: subCategory._id,
                    label: subCategory.name
                  }))}
                  placeholder="Select Sub Category"
                  isDisabled={!selectedCategory}
                  value={
                    field.value
                      ? {
                          value: field.value,
                          label: subCategories?.find(
                            subCategory => subCategory._id === field.value
                          )?.name
                        }
                      : null
                  }
                  onChange={(newValue: unknown) => {
                    const option = newValue as SelectOption;
                    field.onChange(option?.value || null);
                  }}
                />
              )}
            />
            <div className="text-danger">
              {formState.errors.subCategory?.message}
            </div>
          </Col>

          <Col xs={12} sm={6} xl={12}>
            <div className="d-flex gap-2 mb-2">
              <h5 className="mb-0 text-body-highlight">Brand</h5>
            </div>
            <Controller
              name="brand"
              control={control}
              render={({ field }) => (
                <ReactSelect
                  {...field}
                  aria-label="brand"
                  options={brands?.map(brand => ({
                    value: brand._id,
                    label: brand.name
                  }))}
                  placeholder="Select Brand"
                  value={
                    field.value
                      ? {
                          value: field.value,
                          label: brands?.find(
                            brand => brand._id === field.value
                          )?.name
                        }
                      : null
                  }
                  onChange={(newValue: unknown) => {
                    const option = newValue as SelectOption;
                    field.onChange(option?.value || null);
                  }}
                />
              )}
            />
            <div className="text-danger">{formState.errors.brand?.message}</div>
          </Col>

          <Col xs={12} sm={6} xl={12}>
            <div className="d-flex gap-2 mb-2">
              <h5 className="mb-0 text-body-highlight">Tags</h5>
            </div>
            <Controller
              name="tags"
              control={control}
              render={({ field }) => (
                <ReactSelect
                  {...field}
                  aria-label="tags"
                  options={tags?.map(tag => ({
                    value: tag.name,
                    label: tag.name
                  }))}
                  placeholder="Select Tags"
                  isMulti
                  // isDisabled={!selectedIndustry}
                  value={selectedTags?.map(tag => ({
                    value: tag,
                    label: tag
                  }))}
                  onChange={(newValue: unknown) => {
                    const option = newValue as SelectOption[];
                    field.onChange(option?.map(o => o.label) || null);
                  }}
                />
              )}
            />
            <div className="text-danger">{formState.errors.tags?.message}</div>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default OrganizeFormCard;
